/**
 * Epic 12 — turning things elsewhere in the suite into tasks.
 *
 * `capture` makes a task from an email, chat message or doc line and records
 * where it came from; `sources` tells the source which items already became
 * tasks (key and status only for people who can't open them, US 12.7);
 * `meetingItems` are a meeting's action items (US 12.2); `calendarTasks` are
 * my dated tasks for the suite calendar (US 12.3).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient } from "@weco/suite-client";
import { call, type NewTaskBody, type TaskCard, type TaskFull } from "../task/api";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

export type SourceKind = "email" | "chat" | "meeting" | "doc";
/** US 14.4 — a task imported from another tool; its ``url`` is the original. */
export type ImportSourceKind = "jira" | "clickup" | "trello" | "planner";

export interface CaptureSource {
  kind: SourceKind;
  ref?: string;
  thread?: string;
  title?: string;
  sender_name?: string;
  sender_email?: string;
  url?: string;
  /** Everyone who was on the email — the only people who see its body. */
  recipients?: string[];
}

export interface TaskSourceInfo {
  kind: SourceKind | ImportSourceKind;
  ref: string;
  thread: string;
  title: string;
  sender_name: string;
  sender_email: string;
  url: string;
  body: string;
  body_hidden: boolean;
  /** I was on the email / in the chat it came from. */
  on_source: boolean;
}

/** What a source shows about its task. */
export interface TaskBrief {
  id: string | null;
  key: string;
  title: string | null;
  done: boolean;
  status: string;
  status_name: string;
  restricted: boolean;
}

export interface SourceRow {
  ref: string;
  thread: string;
  task: TaskBrief;
}

export type CaptureBody = NewTaskBody & { hub?: string | null; source: CaptureSource; force?: boolean };

export interface MeetingItem {
  id: string;
  position: number;
  text: string;
  owner_email: string;
  owner_name: string;
  due_date: string | null;
  task: TaskBrief;
}

export interface MeetingItems {
  event: string;
  squad: { id: string; name: string; hub: string; hub_name: string; members: { email: string; name: string }[] } | null;
  can_edit: boolean;
  items: MeetingItem[];
}

export interface CalendarTaskSettings {
  show_tasks: boolean;
  hidden_hubs: string[];
}

export interface ChatSquadLink {
  conversation: string;
  /** Null when the chat isn't linked — or is linked to a Squad I can't open (`restricted`). */
  squad: { id: string; name: string; hub_id: string; hub_name: string; color: string } | null;
  restricted: boolean;
}

export const captureApi = {
  capture: (body: CaptureBody) => call(() => client.post<TaskFull & { source: TaskSourceInfo }>("/capture/", body)),
  sources: (kind: SourceKind, by: { refs?: string[]; threads?: string[] }) => {
    const p = new URLSearchParams({ kind });
    if (by.refs?.length) p.set("refs", by.refs.join(","));
    if (by.threads?.length) p.set("threads", by.threads.join(","));
    return call(() => client.get<{ results: SourceRow[] }>(`/task-sources/?${p.toString()}`)).then((r) => r.results);
  },
  taskSource: (taskId: string) => call(() => client.get<TaskSourceInfo>(`/hub-tasks/${taskId}/source/`)),
  /** US 12.6 — the Squad a group chat is linked to. */
  chatSquad: (conversation: string) => call(() => client.get<ChatSquadLink>(`/chat-squad/?conversation=${encodeURIComponent(conversation)}`)),
  linkChatSquad: (conversation: string, squad: string) => call(() => client.put<ChatSquadLink>("/chat-squad/", { conversation, squad })),
  unlinkChatSquad: (conversation: string) => call(() => client.delete<void>(`/chat-squad/?conversation=${encodeURIComponent(conversation)}`)),
  meetingItems: (event: string) => call(() => client.get<MeetingItems>(`/meeting-items/?event=${encodeURIComponent(event)}`)),
  setMeetingSquad: (event: string, squad: string, title: string) =>
    call(() => client.put<MeetingItems>("/meeting-items/squad/", { event, squad, title })),
  addMeetingItem: (event: string, body: { text: string; owner?: string; due_date?: string | null }) =>
    call(() => client.post<MeetingItems>("/meeting-items/", { event, ...body })),
  removeMeetingItem: (id: string, task: "delete" | "keep") => call(() => client.delete<void>(`/meeting-items/${id}/?task=${task}`)),
  calendarTasks: (start: string, end: string) =>
    call(() => client.get<{ results: TaskCard[]; show_tasks: boolean }>(`/calendar-tasks/?start=${start}&end=${end}`)),
  calendarSettings: () => call(() => client.get<CalendarTaskSettings>("/calendar-tasks/settings/")),
  saveCalendarSettings: (body: Partial<CalendarTaskSettings>) => call(() => client.put<CalendarTaskSettings>("/calendar-tasks/settings/", body)),
};

export const SOURCES_KEY = ["tasks", "sources"] as const;

/** Which of these items already became tasks — keyed by ref or thread. */
export function useSources(kind: SourceKind, by: { refs?: string[]; threads?: string[] }, opts: { refetchInterval?: number } = {}) {
  const ids = [...(by.refs ?? []), ...(by.threads ?? [])];
  return useQuery({
    queryKey: [...SOURCES_KEY, kind, by.refs ? "refs" : "threads", ids.join(",")],
    queryFn: () => captureApi.sources(kind, by),
    enabled: ids.length > 0,
    staleTime: 15_000,
    refetchInterval: opts.refetchInterval,
  });
}

export function useTaskSource(taskId: string | null) {
  return useQuery({
    queryKey: ["tasks", "task", taskId ?? "", "source"],
    queryFn: () => captureApi.taskSource(taskId!),
    enabled: !!taskId,
    retry: false,
    staleTime: 60_000,
  });
}

export function useRefreshSources() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: SOURCES_KEY });
    void qc.invalidateQueries({ queryKey: ["tasks", "hub"] });
    void qc.invalidateQueries({ queryKey: ["tasks", "page"] });
    void qc.invalidateQueries({ queryKey: ["tasks", "me"] });
  };
}

// ── The last Squad I put a captured task in (US 12.1) ────────────────────────

const LAST_SQUAD_KEY = "weco.tasks.capture.lastSquad";

export interface LastSquad {
  hub: string;
  squad: string;
}

export function readLastSquad(): LastSquad | null {
  try {
    const raw = JSON.parse(localStorage.getItem(LAST_SQUAD_KEY) || "null") as LastSquad | null;
    return raw && typeof raw.hub === "string" && typeof raw.squad === "string" ? raw : null;
  } catch {
    return null;
  }
}

export function writeLastSquad(value: LastSquad | null) {
  try {
    if (value) localStorage.setItem(LAST_SQUAD_KEY, JSON.stringify(value));
    else localStorage.removeItem(LAST_SQUAD_KEY);
  } catch {
    /* blocked storage: the form simply starts empty next time */
  }
}

// ── US 12.7 — updates my changes owe a task's email or chat ────────────────

export interface SourceUpdate {
  id: string;
  event: "assigned" | "status" | "completed";
  kind: "email" | "chat";
  task: { id: string; key: string; title: string; done: boolean; status_name: string };
  source: { ref: string; thread: string; title: string; sender_name: string; sender_email: string; url: string };
  created_at: string;
}

export type SourceUpdateOutcome = "sent" | "posted" | "dismissed" | "failed";

export const sourceUpdatesApi = {
  pending: () => call(() => client.get<{ results: SourceUpdate[] }>("/source-updates/")),
  handle: (id: string, outcome: SourceUpdateOutcome) =>
    call(() => client.post<{ outcome: string }>(`/source-updates/${id}/`, { outcome })),
};
